// src/modules/portal/services/portalService.ts
// Serviço para buscar empresas no Firestore

import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc,
  limit,
  orderBy,
  WhereFilterOp 
} from 'firebase/firestore';
import { db } from '../../../core/services/firebase/config';
import { TenantListingCard, SearchFilter } from '../../../core/types/portal';
import { Tenant } from '../../../core/types/tenant';

/**
 * Converte um documento Tenant do Firestore em TenantListingCard
 */
const tenantToListingCard = (tenant: Tenant): TenantListingCard => {
  return {
    id: tenant.id,
    slug: tenant.id, // TODO: Adicionar campo slug ao tenant
    businessName: tenant.businessName,
    tenantType: tenant.tenantType,
    location: tenant.location,
    shortDescription: tenant.portalProfile.shortDescription,
    coverImage: tenant.portalProfile.coverImage,
    highlights: tenant.portalProfile.highlights,
    categoryTags: tenant.categoryTags,
    averageRating: undefined, // TODO: Calcular média de reviews
    reviewCount: undefined,
    priceRange: undefined, // TODO: Calcular do catálogo de serviços
    isFeatured: tenant.portalProfile.isFeatured || false,
    verifiedBadge: tenant.portalProfile.verifiedBadge || false,
    websiteUrl: tenant.domain ? `https://${tenant.domain}` : undefined,
    whatsappNumber: tenant.portalProfile.contact.whatsapp,
  };
};

/**
 * Busca empresas por estado
 */
// Busca todos os tenants (para listagens gerais)
export const getAllTenants = async (): Promise<TenantListingCard[]> => {
  const tenantsRef = collection(db, 'tenants');
  const q = query(
    tenantsRef,
    where('status', '==', 'active')
  );
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => {
    const data = doc.data() as Tenant;
    return {
      id: data.id,
      slug: data.domain || data.id, // Fallback
      businessName: data.businessName,
      tenantType: data.tenantType,
      location: data.location,
      shortDescription: data.portalProfile?.shortDescription || '',
      highlights: data.portalProfile?.highlights || [],
      categoryTags: data.categoryTags || [],
      coverImage: data.portalProfile?.coverImage,
      isFeatured: data.portalProfile?.isFeatured || false,
      verifiedBadge: data.portalProfile?.verifiedBadge || false,
      reviewStats: { rating: 0, count: 0 } 
    };
  });
};

export const getTenantsByState = async (stateCode: string): Promise<TenantListingCard[]> => {
  try {
    const tenantsRef = collection(db, 'tenants');
    
    // Query simplificada - busca todos os tenants do estado
    // Ordenação client-side para evitar dependência de índices complexos
    const q = query(
      tenantsRef,
      where('location.state', '==', stateCode.toUpperCase())
    );
    
    const querySnapshot = await getDocs(q);
    const tenants: TenantListingCard[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data() as Tenant;
      tenants.push(tenantToListingCard({ ...data, id: doc.id }));
    });
    
    // Ordenação client-side: Featured primeiro, depois por nome
    tenants.sort((a, b) => {
      if (a.isFeatured && !b.isFeatured) return -1;
      if (!a.isFeatured && b.isFeatured) return 1;
      return a.businessName.localeCompare(b.businessName);
    });
    
    return tenants;
  } catch (error) {
    console.error('Error fetching tenants by state:', error);
    return [];
  }
};

/**
 * Busca empresas por cidade
 */
export const getTenantsByCity = async (stateCode: string, city: string): Promise<TenantListingCard[]> => {
  try {
    const tenantsRef = collection(db, 'tenants');
    const q = query(
      tenantsRef,
      where('location.state', '==', stateCode.toUpperCase()),
      where('location.city', '==', city),
      orderBy('portalProfile.isFeatured', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const tenants: TenantListingCard[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data() as Tenant;
      tenants.push(tenantToListingCard({ ...data, id: doc.id }));
    });
    
    return tenants;
  } catch (error) {
    console.error('Error fetching tenants by city:', error);
    return [];
  }
};

/**
 * Busca empresas em destaque (para home)
 */
export const getFeaturedTenants = async (maxResults: number = 8): Promise<TenantListingCard[]> => {
  try {
    const tenantsRef = collection(db, 'tenants');
    const q = query(
      tenantsRef,
      where('portalProfile.isFeatured', '==', true),
      orderBy('portalProfile.featuredOrder', 'asc'),
      limit(maxResults)
    );
    
    const querySnapshot = await getDocs(q);
    const tenants: TenantListingCard[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data() as Tenant;
      tenants.push(tenantToListingCard({ ...data, id: doc.id }));
    });
    
    return tenants;
  } catch (error) {
    console.error('Error fetching featured tenants:', error);
    return [];
  }
};

/**
 * Busca um tenant por ID ou slug
 */
export const getTenantBySlug = async (slug: string): Promise<Tenant | null> => {
  try {
    // Primeiro tenta buscar por ID
    const docRef = doc(db, 'tenants', slug);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { ...docSnap.data(), id: docSnap.id } as Tenant;
    }
    
    // TODO: Implementar busca por slug quando campo for adicionado
    return null;
  } catch (error) {
    console.error('Error fetching tenant by slug:', error);
    return null;
  }
};

/**
 * Registra uma validação da comunidade ("Eu Conheço")
 */
export const vouchForTenant = async (tenantId: string, userId: string): Promise<boolean> => {
  try {
    // TODO: Implementar chamada real para Cloud Function
    // await cloudFunctions.call('vouchForTenant', { tenantId });
    
    // Simulação do comportamento esperado no backend:
    // 1. Adicionar ID do usuário ao array communityVouches
    // 2. Verificar se atingiu o limite para auto-promoção (ex: 5 vouches)
    // 3. Se sim, atualizar trustLevel para 'community-vouched'
    
    console.log(`[MOCK] Vouch registrado para tenant ${tenantId} pelo usuário ${userId}`);
    return true;
  } catch (error) {
    console.error('Error vouching for tenant:', error);
    return false;
  }
};

import { algoliaService } from '../../../core/services/algolia/algoliaService';

/**
 * Busca empresas com filtros avançados
 */
export const searchTenants = async (filters: SearchFilter): Promise<TenantListingCard[]> => {
  // 1. Tentar busca no Algolia primeiro
  const algoliaResults = await algoliaService.search(filters);
  if (algoliaResults !== null) {
    return algoliaResults;
  }

  // 2. Fallback para Firestore se Algolia falhar ou não estiver configurado
  try {
    const tenantsRef = collection(db, 'tenants');
    let q = query(tenantsRef);
    
    // Filtro por estado
    if (filters.state) {
      q = query(q, where('location.state', '==', filters.state.toUpperCase()));
    }
    
    // Filtro por cidade
    if (filters.city) {
      q = query(q, where('location.city', '==', filters.city));
    }
    
    // Filtro por tipo de empresa
    if (filters.tenantTypes && filters.tenantTypes.length > 0) {
      q = query(q, where('tenantType', 'in', filters.tenantTypes));
    }
    
    // Filtro por categoria (tags)
    if (filters.categoryTags && filters.categoryTags.length > 0) {
      // Firestore suporta array-contains-any
      q = query(q, where('categoryTags', 'array-contains-any', filters.categoryTags));
    }
    
    // Ordenação
    switch (filters.sortBy) {
      case 'rating':
        // TODO: Implementar quando tiver campo de rating agregado
        break;
      case 'price-asc':
      case 'price-desc':
        // TODO: Implementar quando tiver campo de preço
        break;
      case 'recent':
        q = query(q, orderBy('createdAt', 'desc'));
        break;
      default:
        // Relevância: featured primeiro
        q = query(q, orderBy('portalProfile.isFeatured', 'desc'));
    }
    
    const querySnapshot = await getDocs(q);
    let tenants: TenantListingCard[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data() as Tenant;
      tenants.push(tenantToListingCard({ ...data, id: doc.id }));
    });
    
    // Filtros client-side (quando Firestore não suporta)
    
    // Filtro por busca textual
    if (filters.query) {
      const searchLower = filters.query.toLowerCase();
      tenants = tenants.filter(t => 
        t.businessName.toLowerCase().includes(searchLower) ||
        t.shortDescription.toLowerCase().includes(searchLower) ||
        t.location.city.toLowerCase().includes(searchLower)
      );
    }
    
    // Filtro por avaliação mínima
    if (filters.minRating && filters.minRating > 0) {
      tenants = tenants.filter(t => (t.averageRating || 0) >= filters.minRating!);
    }
    
    return tenants;
  } catch (error) {
    console.error('Error searching tenants:', error);
    return [];
  }
};

/**
 * Busca estatísticas do portal (para exibir na home)
 */
export const getPortalStats = async () => {
  try {
    const tenantsRef = collection(db, 'tenants');
    const querySnapshot = await getDocs(tenantsRef);
    
    let totalTenants = 0;
    const statesSet = new Set<string>();
    const typeCount: Record<string, number> = {};
    
    querySnapshot.forEach((doc) => {
      const data = doc.data() as Tenant;
      totalTenants++;
      
      if (data.location?.state) {
        statesSet.add(data.location.state);
      }
      
      if (data.tenantType) {
        typeCount[data.tenantType] = (typeCount[data.tenantType] || 0) + 1;
      }
    });
    
    return {
      totalTenants,
      totalStates: statesSet.size,
      totalReviews: 0, // TODO: Agregar de coleção de reviews
      averageRating: 4.8, // TODO: Calcular média real
      tenantsByType: typeCount,
    };
  } catch (error) {
    console.error('Error fetching portal stats:', error);
    return {
      totalTenants: 0,
      totalStates: 0,
      totalReviews: 0,
      averageRating: 0,
      tenantsByType: {},
    };
  }
};

/**
 * Interface para município (igual ao salvo no Firestore)
 */
export interface FirestoreMunicipality {
  code: string;
  name: string;
  stateCode: string;
  stateName: string;
  touristicRegion?: string;
  category?: string;
}

/**
 * Busca todos os municípios de um estado (ordenados por nome)
 */
export const getMunicipalitiesByState = async (stateCode: string): Promise<FirestoreMunicipality[]> => {
  try {
    const municipalitiesRef = collection(db, 'municipalities');
    const q = query(
      municipalitiesRef,
      where('stateCode', '==', stateCode.toUpperCase()),
      orderBy('name', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    const municipalities: FirestoreMunicipality[] = [];
    
    querySnapshot.forEach((doc) => {
      // Remove campos de controle interno se houver
      const { code, name, stateCode, stateName, touristicRegion, category } = doc.data() as FirestoreMunicipality;
      municipalities.push({ code, name, stateCode, stateName, touristicRegion, category });
    });
    
    return municipalities;
  } catch (error) {
    console.error(`Error fetching municipalities for state ${stateCode}:`, error);
    return [];
  }
};
